import {
  type AlgoliaRecord,
  type ListPrice,
  type RetailPrice,
} from '../../../algolia'

import { productSearchAlgoliaAccessorIsPricePresent } from './productSearchAlgoliaAccessorIsPricePresent'

interface ProductSearchAlgoliaAccessorGetPricingData {
  isListPricePresent: boolean
  isPriceStrikedThrough: boolean
  isRetailPricePresent: boolean
  listPriceAmount?: number
  listPriceFormatted?: string
  pricePerUnitFormatted?: string
  productCardFormattedPrice?: string
  retailPriceFormatted?: string
  shouldShowPriceStartingFrom: boolean
}

/*
  If we have prescription medicine product then we don't use the strike through design method.
  We also do another check if the initial price is the same with the new price to prevent from showing two times the same price on the Result Item card.
 */
const isPriceStrikedThrough = (algoliaProduct: AlgoliaRecord): boolean => {
  const {
    listPrice,
    prescriptionMedicine: isPrescriptionMedicine,
    price: retailPrice,
  } = algoliaProduct

  return (
    (!retailPrice || !listPrice || listPrice !== retailPrice) &&
    !isPrescriptionMedicine
  )
}

type ListAndRetailPrices = {
  listPrice: ListPrice | undefined
  retailPrice: RetailPrice
}

/*
  We have to check if the listPrice is indeed bigger than the retail price.
  We can use discount data but adding this additional layer we prevent having duplicate price on the Result Item card.
 */
const isListPriceGreaterThanOrEqualToRetailPrice = ({
  listPrice,
  retailPrice,
}: ListAndRetailPrices): boolean => {
  if (!listPrice) {
    return false
  }

  return (
    productSearchAlgoliaAccessorIsPricePresent(listPrice) &&
    productSearchAlgoliaAccessorIsPricePresent(retailPrice) &&
    listPrice.amount > retailPrice.amount
  )
}

const showPriceStartingFrom = (algoliaProduct: AlgoliaRecord): boolean => {
  const { variants } = algoliaProduct

  // TO DO - handle the shouldShowProductVariantsBox() function which handles another aspect of the ResultItem card
  // this condition should look something like:
  //  return Boolean(variants && variants.length > 1 && !shouldShowProductVariantsBox())
  return Boolean(variants && variants.length > 1)
}

export const productSearchAlgoliaAccessorGetPricingData = (
  algoliaProduct: AlgoliaRecord,
): ProductSearchAlgoliaAccessorGetPricingData => {
  const { priceFormatted, pricePerUnit, prices = undefined } = algoliaProduct

  if (!prices) {
    return {
      isListPricePresent: false,
      isPriceStrikedThrough: false,
      isRetailPricePresent: false,
      shouldShowPriceStartingFrom: false,
    }
  }

  const { listPrice, retailPrice } = prices

  const isListPricePresent = isListPriceGreaterThanOrEqualToRetailPrice({
    listPrice,
    retailPrice,
  })

  return {
    isListPricePresent,
    isPriceStrikedThrough: isPriceStrikedThrough(algoliaProduct),
    isRetailPricePresent:
      productSearchAlgoliaAccessorIsPricePresent(retailPrice),
    listPriceAmount: listPrice?.amount,
    listPriceFormatted: listPrice?.formatted,
    pricePerUnitFormatted: pricePerUnit,
    productCardFormattedPrice: priceFormatted,
    retailPriceFormatted: retailPrice.formatted,
    shouldShowPriceStartingFrom: showPriceStartingFrom(algoliaProduct),
  }
}
